import React, { FC, ReactNode, Suspense } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import * as analytics from '@adsk/acs-web-analytics';
import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';
import LazyComponent from './LazyComponent';
import { ANALYTICS_TYPES } from '../../analytics';
import AppContentError from '../AppContentError';
import AppContentLoader from '../AppContentLoader';

const REPORT_ERROR_TYPE = 'remote-module-error';

type RemoteComponentProps = {
  url: string;
  module: string;
  scope: string;
  toolId?: string;
  renderLoader?: () => NonNullable<ReactNode> | null;
  children?: ReactNode;
};

const RemoteComponent: FC<RemoteComponentProps> = ({
  renderLoader = () => <AppContentLoader />,
  children,
  ...props
}) => {
  const { module, scope, url, toolId } = props;

  return (
    <ErrorBoundary
      fallbackRender={() => <AppContentError />}
      resetKeys={[url, module, scope, children, toolId]}
      onError={(err) => {
        analytics.track(ANALYTICS_TYPES.EXO_TOOL_INTERNAL_ERROR, {
          navigate_url: window.location.href,
          remote_entry_url: url,
          requested_tool: scope,
        });

        return report({
          type: REPORT_TYPES.error,
          value: err,
          context: {
            type: REPORT_ERROR_TYPE,
            scope,
            module,
          },
        });
      }}
    >
      <Suspense fallback={renderLoader()}>
        <LazyComponent {...props}>{children}</LazyComponent>
      </Suspense>
    </ErrorBoundary>
  );
};

RemoteComponent.propTypes = {
  url: PropTypes.string.isRequired,
  toolId: PropTypes.string,
  module: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  children: PropTypes.node,
  renderLoader: PropTypes.func,
};

export default RemoteComponent;
